import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetHospitalQuery } from "../services/hospitalService";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Edit,
  ArrowBack as ArrowBackIcon,
  LocalHospital,
  Phone,
  LocationOn,
  Map,
  AccessTime,
  Email,
} from "@mui/icons-material";
import MapPicker from "../components/MapPicker";
import HospitalFormModal from "../components/HospitalFormModal";

const Hospital = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const {
    data: hospital,
    error,
    isFetching,
    refetch,
  } = useGetHospitalQuery(id as string);

  const handleAddSuccess = () => {
    refetch();
    setIsEditModalOpen(false);
  };

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading user data. Please try again later.
      </Alert>
    );
  }
  if (!hospital) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        hospital not found
      </Alert>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box
      sx={{ p: 3, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Hospital Details
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Edit />}
          sx={{ ml: "auto" }}
          onClick={() => setIsEditModalOpen(true)}
        >
          Edit Hospital
        </Button>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          {/** Left: Hospital details */}
          <Grid size={12}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <LocalHospital fontSize="large" />
                  <Box>
                    <Typography variant="h6" component="h1">
                      {hospital.name}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                      {hospital.address.formattedAddress}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} alignItems={"center"}>
                    <Tooltip title="Contact phone">
                      <IconButton size="small" aria-label="phone" disabled>
                        <Phone fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2">
                      {hospital.phone ?? "-"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems={"center"}>
                    <Tooltip title="Contact email">
                      <IconButton size="small" aria-label="email" disabled>
                        <Email fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2">
                      {hospital.email ?? "-"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems={"center"}>
                    <Tooltip title="Address">
                      <IconButton size="small" aria-label="address" disabled>
                        <LocationOn fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="body2">
                      {hospital.address.formattedAddress}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} alignItems={"center"}>
                  <Chip
                    label={`Hospital ID: ${hospital._id ?? "-"}`}
                    size="small"
                  />
                  <Chip
                    label={
                      hospital.totalBeds
                        ? ` Total Beds: ${hospital.totalBeds}`
                        : "Beds: N/A"
                    }
                    size="small"
                  />
                  <Chip
                    label={
                      hospital.icuBeds
                        ? ` ICU Beds: ${hospital.icuBeds}`
                        : "ICU Beds: N/A"
                    }
                    size="small"
                  />
                  <Chip
                    label={
                      hospital.ventilatorBeds
                        ? ` Ventilator Beds: ${hospital.ventilatorBeds}`
                        : "Ventilator Beds: N/A"
                    }
                    size="small"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/**Right: Bed details */}
          <Grid size={12}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">Bed availability</Typography>
                  <Chip
                    icon={<Map />}
                    label={
                      hospital.totalBeds
                        ? `${
                            (hospital.availableBeds / hospital.totalBeds) * 100
                          }%`
                        : "N/A"
                    }
                    size="small"
                  />
                </Stack>

                <Stack spacing={2}>{}</Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Quick snapshot
                </Typography>

                <Grid container spacing={1}>
                  <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 1 }}>
                      <Typography variant="caption">Free Beds</Typography>
                      <Typography variant="h6">
                        {hospital.availableBeds}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 1 }}>
                      <Typography variant="caption">Free ICU Beds</Typography>
                      <Typography variant="h6">
                        {hospital.icuAvailableBeds}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid size={12}>
                    <Card variant="outlined" sx={{ p: 1 }}>
                      <Typography variant="caption">
                        Free Ventilator Beds
                      </Typography>
                      <Typography variant="h6">
                        {hospital.ventilatorAvailableBeds}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/**Botton: Opening hours + Map (full width) */}
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <AccessTime />
                      <Typography variant="h6">Opening hours</Typography>
                    </Stack>

                    <Table size="small" aria-label="opening hours">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>Day</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Opening Time
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Closing Time
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.keys(hospital.operatingHours).map(
                          (key: string) => (
                            <TableRow key={key}>
                              <TableCell
                                component="th"
                                sx={{ borderBottom: "none", py: 0.5 }}
                              >
                                <Typography variant="caption">
                                  {key.toUpperCase()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {hospital.operatingHours[key].start + "Hrs"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {hospital.operatingHours[key].end + "Hrs"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={8}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 0, height: { xs: 300, md: 200 } }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      <MapPicker
                        onLocationSelect={() => {}}
                        initialLocation={{
                          lat: hospital.location?.coordinates?.[1] || 0,
                          lng: hospital.location?.coordinates?.[0] || 0,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <HospitalFormModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleAddSuccess}
        hospital={hospital}
      />
    </Box>
  );
};

export default Hospital;
